import { getMomentTz, MesfModal } from "@dexteel/mesf-core";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
	Alert,
	alpha,
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Divider,
	InputAdornment,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Fragment,
	memo,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";

import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
	type BundleReclassification,
	getBundleReclassification,
	getSawReclassifyProducts,
	type SawReclassifyProduct,
} from "../repositories/sawReclassifyRepository";
import {
	getSawRecentBundles,
	reclassifyBundle,
	revertBundleReclassification,
	type SawRecentBundle,
} from "../repositories/sawWriteRepository";
import {
	rangeWithFraction32,
	toFraction32,
	withFraction32,
} from "@/utils/fraction";
import {
	capsSx,
	InfoGrid,
	type InfoGridItem,
	StateChip,
	valueSx,
} from "./primitives";

const moment = getMomentTz();

type Props = {
	open: boolean;
	assetId: number | null;
	bundleId: number | null;
	onClose: () => void;
	onReclassified?: () => void;
	// tablet only; PC leaves it null
	employeeNumber?: string | null;
	// tablet: big targets, fixed bundle, no keyboard on open
	touch?: boolean;
};

const fmtIn = (v: number | null): string => (v != null ? `${v}"` : "—");

const bundleDetail = (b: SawRecentBundle) =>
	`${moment(b.StartTime).format("HH:mm DD/MM")} – ${
		b.EndTime ? moment(b.EndTime).format("HH:mm DD/MM") : "Now"
	}`;

const bundleLabel = (b: SawRecentBundle) =>
	`${b.BarCount ?? "—"}p · ${withFraction32(b.AvgLength, "—")}`;

const bundleChips = (b: SawRecentBundle) => (
	<>
		{b.IsCutback && <StateChip label="Cutback" color="#757575" />}
		{b.EndTime == null && <StateChip label="In progress" color="#b58900" />}
		{b.HasCheck && <StateChip label="Checked" color="#3D5E9E" />}
	</>
);

const fractionCaptionSx = {
	display: "block",
	lineHeight: 1.1,
	fontVariantNumeric: "tabular-nums",
} as const;

const FractionCaption = ({ value }: { value: number }) => (
	<Typography variant="caption" color="text.secondary" sx={fractionCaptionSx}>
		{toFraction32(value)}
	</Typography>
);

// The latest check's positions — the fit is judged on these.
const checkValues = (b: SawRecentBundle | null) =>
	(
		[
			["TL", b?.CheckTL],
			["TR", b?.CheckTR],
			["MC", b?.CheckMC],
			["BL", b?.CheckBL],
			["BR", b?.CheckBR],
		] as const
	).filter(([, v]) => v != null) as [string, number][];

const measuredDetail = (b: SawRecentBundle | null): ReactNode | null => {
	const parts = checkValues(b);
	if (parts.length === 0) return null;
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
			{parts.map(([k, v], i) => (
				<Fragment key={k}>
					{i > 0 && <Divider orientation="vertical" flexItem />}
					<Box sx={{ textAlign: "center" }}>
						<span>{`${k} ${v}"`}</span>
						<FractionCaption value={v as number} />
					</Box>
				</Fragment>
			))}
		</Box>
	);
};

// Tablet: label over value, one size for every value, never truncated.
const Stat = ({
	label,
	value,
	sub,
}: {
	label: string;
	value: ReactNode;
	sub?: string;
}) => (
	<Box>
		<Typography sx={capsSx}>{label}</Typography>
		<Typography sx={{ ...valueSx, fontSize: 15 }}>{value}</Typography>
		{sub && (
			<Typography
				sx={{ fontSize: 11, color: "text.secondary", whiteSpace: "nowrap" }}
			>
				{sub}
			</Typography>
		)}
	</Box>
);

const spreadSx = {
	display: "flex",
	flexWrap: "wrap",
	justifyContent: "space-between",
	columnGap: 1.5,
} as const;

const TabletBundleSummary = ({
	bundle,
}: {
	bundle: SawRecentBundle | null;
}) => {
	const checks = checkValues(bundle);
	const avg = bundle?.AvgLength;
	const min = bundle?.MinLength;
	const max = bundle?.MaxLength;
	const tol = min != null && max != null;
	return (
		<Box
			sx={{
				mb: 1.5,
				p: 1.5,
				bgcolor: "grey.50",
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
				// one row each in portrait, spread edge to edge
				...spreadSx,
				rowGap: 1.5,
			}}
		>
			<Stat
				label="Part"
				value={
					bundle?.PartNumber ??
					(bundle?.IsCutback ? "Cutback" : "No work order")
				}
			/>
			<Stat label="Pieces" value={bundle?.BarCount ?? "—"} />
			<Stat label="Section" value={bundle?.SectionName ?? "—"} />
			<Stat label="Grade" value={bundle?.GradeName ?? "—"} />
			<Stat
				label="Cut"
				value={bundle ? moment(bundle.StartTime).format("HH:mm") : "—"}
			/>
			<Stat
				label="Cut length"
				value={avg != null ? `${avg.toFixed(2)}″` : "—"}
				sub={avg != null ? toFraction32(avg) : undefined}
			/>
			<Stat
				label="Tolerance"
				value={tol ? `${min}″ – ${max}″` : "None"}
				sub={tol ? `${toFraction32(min)} – ${toFraction32(max)}` : undefined}
			/>
			<Box
				sx={{
					flexBasis: "100%",
					...spreadSx,
					rowGap: 1,
					pt: 1.5,
					borderTop: 1,
					borderColor: "divider",
				}}
			>
				{checks.length === 0 ? (
					<Stat label="Quality check" value="No check" />
				) : (
					checks.map(([position, value]) => (
						<Stat
							key={position}
							label={position}
							value={`${value}″`}
							sub={toFraction32(value)}
						/>
					))
				)}
			</Box>
		</Box>
	);
};

// Part | Min | Target | Max | Fits
const COLS = "1fr 80px 80px 80px 48px";

const lenValueSx = { fontVariantNumeric: "tabular-nums" } as const;
const lenValueBoldSx = { ...lenValueSx, fontWeight: 700 } as const;

// Decimal inches with the 1/32" fraction underneath (plant reads tapes).
const LenCell = ({ value, bold }: { value: number | null; bold?: boolean }) => (
	<Box sx={{ textAlign: "right" }}>
		<Typography variant="body2" sx={bold ? lenValueBoldSx : lenValueSx}>
			{fmtIn(value)}
		</Typography>
		{value != null && <FractionCaption value={value} />}
	</Box>
);

const HeaderRow = ({ touch }: { touch: boolean }) => (
	<Box
		sx={{
			display: "grid",
			gridTemplateColumns: COLS,
			gap: 1,
			px: 1.5,
			py: touch ? 1 : 0.5,
			backgroundColor: "grey.100",
			position: "sticky",
			top: 0,
			zIndex: 1,
			"& > *": {
				fontSize: touch ? "0.8rem" : "0.65rem",
				fontWeight: 700,
				letterSpacing: "0.05em",
				textTransform: "uppercase",
				color: "text.secondary",
			},
		}}
	>
		<Typography component="span">Part</Typography>
		<Typography component="span" sx={{ textAlign: "right" }}>
			Min
		</Typography>
		<Typography component="span" sx={{ textAlign: "right" }}>
			Target
		</Typography>
		<Typography component="span" sx={{ textAlign: "right" }}>
			Max
		</Typography>
		<Typography component="span" sx={{ textAlign: "center" }}>
			Fits
		</Typography>
	</Box>
);

const ProductRow = memo(function ProductRow({
	product,
	selected,
	onSelect,
	touch,
}: {
	product: SawReclassifyProduct;
	selected: boolean;
	onSelect: (p: SawReclassifyProduct) => void;
	touch: boolean;
}) {
	const eligible = product.IsFit === 1;
	return (
		<Box
			onClick={eligible ? () => onSelect(product) : undefined}
			sx={{
				display: "grid",
				gridTemplateColumns: COLS,
				gap: 1,
				alignItems: "center",
				px: 1.5,
				py: 0.75,
				minHeight: touch ? 60 : undefined,
				borderBottom: "1px solid",
				borderColor: "divider",
				cursor: eligible ? "pointer" : "default",
				opacity: eligible ? 1 : 0.45,
				backgroundColor: (t) =>
					selected
						? alpha(t.palette.primary.main, touch ? 0.22 : 0.12)
						: "transparent",
				boxShadow: (t) =>
					selected
						? `inset ${touch ? 6 : 3}px 0 0 ${t.palette.primary.main}`
						: "none",
				// touch: tap leaves a sticky hover
				...(eligible &&
					!touch && {
						"&:hover": {
							backgroundColor: (t) =>
								selected ? alpha(t.palette.primary.main, 0.12) : "action.hover",
						},
					}),
			}}
		>
			<Box sx={{ minWidth: 0 }}>
				<Typography
					fontWeight={700}
					sx={{
						fontSize: touch ? "1.05rem" : "0.85rem",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{product.PartNumber ?? "—"}
				</Typography>
				{product.ExtendedPartNumber &&
					product.ExtendedPartNumber !== product.PartNumber && (
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{
								display: "block",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
						>
							{product.ExtendedPartNumber}
						</Typography>
					)}
			</Box>
			<LenCell value={product.MinLength} />
			<LenCell value={product.TargetLength} bold />
			<LenCell value={product.MaxLength} />
			<Box sx={{ textAlign: "center", lineHeight: 0 }}>
				{eligible ? (
					<CheckIcon fontSize="small" color="success" />
				) : (
					<CloseIcon fontSize="small" color="error" />
				)}
			</Box>
		</Box>
	);
});

// Re-home a bundle to a saw product whose min/max window contains its measured
// length (same section + grade), or send it to Cutback when nothing fits.
// Non-fitting products are listed but disabled. The bundle selector preselects
// the caller's bundle (outgoing card / checks-table row) and allows switching.
export const ReclassifyBundleModal = ({
	open,
	assetId,
	bundleId,
	onClose,
	onReclassified,
	employeeNumber = null,
	touch = false,
}: Props) => {
	const queryClient = useQueryClient();
	const [search, setSearch] = useState("");
	const [selected, setSelected] = useState<SawReclassifyProduct | null>(null);
	const [selectedBundle, setSelectedBundle] = useState<SawRecentBundle | null>(
		null,
	);
	const [confirmCutback, setConfirmCutback] = useState(false);
	const [confirmRestore, setConfirmRestore] = useState(false);
	const [saving, setSaving] = useState(false);
	const [actionError, setActionError] = useState<string | null>(null);

	// Both gantts read the reclassification (grey Cutback bars); refresh them
	// right away instead of waiting for their 60s polling.
	const refreshGantts = async () => {
		await queryClient.invalidateQueries({ queryKey: ["sawGanttData"] });
		await queryClient.invalidateQueries({ queryKey: ["ganttData"] });
	};

	useEffect(() => {
		if (!open) {
			setSearch("");
			setSelected(null);
			setSelectedBundle(null);
			setConfirmCutback(false);
			setConfirmRestore(false);
			setActionError(null);
		}
	}, [open]);

	const {
		data: bundles = [],
		isFetching: fetchingBundles,
		isSuccess: bundlesLoaded,
		error: bundlesError,
		refetch: refetchBundles,
	} = useQuery<SawRecentBundle[], Error>({
		queryKey: ["sawRecentBundles", assetId, bundleId],
		queryFn: () => getSawRecentBundles(assetId as number, bundleId),
		enabled: open && assetId != null,
	});

	// Preselect the caller's bundle (outgoing card / checks-table row), else the
	// most recent one; the selector lets the operator switch. Never fall back to
	// another bundle when one was asked for — that reclassifies the wrong one.
	useEffect(() => {
		if (
			!open ||
			fetchingBundles ||
			!bundlesLoaded ||
			selectedBundle != null ||
			bundles.length === 0
		)
			return;
		setSelectedBundle(
			bundles.find((b) => b.BundleId === bundleId) ??
				(bundleId == null ? bundles[0] : null),
		);
	}, [open, bundles, bundleId, selectedBundle, fetchingBundles, bundlesLoaded]);

	const effectiveBundleId = selectedBundle?.BundleId ?? null;
	const fixedBundle = touch && bundleId != null;

	// Already reclassified → offer to undo back to the original work order.
	const {
		data: reclassification = null,
		isFetching: fetchingDisposition,
		isSuccess: dispositionLoaded,
		error: dispositionError,
		refetch: refetchDisposition,
	} = useQuery<BundleReclassification | null, Error>({
		queryKey: ["bundleReclassification", effectiveBundleId],
		queryFn: () => getBundleReclassification(effectiveBundleId as number),
		enabled: open && effectiveBundleId != null,
		meta: { realtime: ["FAB.GetBundleReclassification"] },
	});

	const {
		data: products = [],
		isFetching: fetchingProducts,
		isSuccess: productsLoaded,
		error: productsError,
		refetch: refetchProducts,
	} = useQuery<SawReclassifyProduct[], Error>({
		queryKey: ["sawReclassifyProducts", assetId, effectiveBundleId],
		queryFn: () =>
			getSawReclassifyProducts(assetId as number, effectiveBundleId),
		enabled: open && assetId != null && effectiveBundleId != null,
	});
	const loading = fetchingBundles || fetchingDisposition || fetchingProducts;
	const loadError = bundlesError ?? dispositionError ?? productsError;
	const requestedBundleMissing =
		bundlesLoaded &&
		!fetchingBundles &&
		selectedBundle == null &&
		bundleId != null &&
		!bundles.some((bundle) => bundle.BundleId === bundleId);
	const actionsDisabled =
		!open ||
		saving ||
		loading ||
		!bundlesLoaded ||
		!dispositionLoaded ||
		!productsLoaded ||
		effectiveBundleId == null;
	const retryLoad = async () => {
		setSelected(null);
		const requests: Promise<unknown>[] = [];
		if (bundlesError || requestedBundleMissing) requests.push(refetchBundles());
		if (effectiveBundleId != null) {
			if (dispositionError) requests.push(refetchDisposition());
			if (productsError) requests.push(refetchProducts());
		}
		await Promise.all(requests);
	};

	// The measured positions get a full row so all five values fit spelled out.
	const bundleInfo: InfoGridItem[] = [
		{
			label: "Part",
			value:
				selectedBundle?.PartNumber ??
				(selectedBundle?.IsCutback ? "Cutback" : "No work order"),
		},
		{
			label: "Pieces",
			value:
				selectedBundle?.BarCount != null
					? String(selectedBundle.BarCount)
					: null,
		},
		{ label: "Section", value: selectedBundle?.SectionName ?? null },
		{ label: "Grade", value: selectedBundle?.GradeName ?? null },
		{
			label: "Cut Length",
			value:
				selectedBundle?.AvgLength != null
					? withFraction32(selectedBundle.AvgLength)
					: null,
		},
		{
			label: "Tolerance",
			value:
				selectedBundle?.MinLength != null && selectedBundle?.MaxLength != null
					? rangeWithFraction32(
							selectedBundle.MinLength,
							selectedBundle.MaxLength,
						)
					: "No specs",
		},
		{
			label: "Quality Check",
			value: measuredDetail(selectedBundle) ?? "No check",
			span: true,
		},
	];

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return products;
		return products.filter(
			(p) =>
				(p.PartNumber ?? "").toLowerCase().includes(q) ||
				(p.ExtendedPartNumber ?? "").toLowerCase().includes(q),
		);
	}, [products, search]);

	const emptyText =
		loadError || requestedBundleMissing
			? "Retry to load bundle details."
			: effectiveBundleId == null
				? bundles.length === 0
					? "No bundles available"
					: "Select a bundle to see matching products"
				: filtered.length === 0
					? "No products match the search"
					: null;

	const toggleSelected = useCallback(
		(p: SawReclassifyProduct) =>
			setSelected((prev) => (prev?.ProductId === p.ProductId ? null : p)),
		[],
	);

	const save = async (cutback: boolean) => {
		if (
			actionsDisabled ||
			effectiveBundleId == null ||
			(!cutback && selected == null)
		)
			return;
		setSaving(true);
		setActionError(null);
		try {
			await reclassifyBundle(
				effectiveBundleId,
				cutback ? null : (selected?.ProductId ?? null),
				cutback,
				employeeNumber,
			);
			await refreshGantts();
			onReclassified?.();
			onClose();
		} catch (e) {
			setActionError(
				e instanceof Error ? e.message : "Unable to reclassify the bundle.",
			);
		} finally {
			setSaving(false);
		}
	};

	const restore = async () => {
		if (actionsDisabled || effectiveBundleId == null) return;
		setSaving(true);
		setActionError(null);
		try {
			await revertBundleReclassification(effectiveBundleId, employeeNumber);
			await refreshGantts();
			onReclassified?.();
			onClose();
		} catch (e) {
			setActionError(
				e instanceof Error ? e.message : "Unable to restore the original part.",
			);
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<MesfModal
				id="modal-reclassify-bundle"
				title="RECLASSIFY BUNDLE"
				open={open}
				handleClose={onClose}
				maxWidth={touch ? "md" : "sm"}
			>
				<MesfModal.Content
					style={{
						padding: "15px 24px",
						// touch: md width, full height, list takes the rest
						...(touch && {
							width: "100vw",
							maxWidth: "100%",
							height: "100vh",
							display: "flex",
							flexDirection: "column",
						}),
					}}
				>
					{(loadError || requestedBundleMissing) && (
						<Alert
							severity="error"
							sx={{ mb: 1.5 }}
							action={
								<Button disabled={loading} onClick={() => void retryLoad()}>
									Retry
								</Button>
							}
						>
							{loadError?.message ??
								(fixedBundle
									? "The selected bundle is no longer available. Retry."
									: "The selected bundle is no longer available. Select another bundle or retry.")}
						</Alert>
					)}
					{actionError && (
						<Alert
							severity="error"
							sx={{ mb: 1.5 }}
							onClose={() => setActionError(null)}
						>
							{actionError}
						</Alert>
					)}
					{!fixedBundle && (
						<Autocomplete
							size="small"
							options={bundles}
							value={selectedBundle}
							loading={fetchingBundles}
							disabled={saving || fetchingBundles || !bundlesLoaded}
							disableClearable={selectedBundle != null}
							onChange={(_, next) => {
								setSelectedBundle(next);
								setSelected(null);
								setActionError(null);
							}}
							getOptionLabel={(b) => `${bundleLabel(b)} · ${bundleDetail(b)}`}
							isOptionEqualToValue={(a, b) => a.BundleId === b.BundleId}
							renderOption={({ key, ...props }, b) => (
								<Box
									component="li"
									key={key}
									{...props}
									sx={{ display: "flex", alignItems: "center", gap: 1 }}
								>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography fontWeight={700} sx={{ fontSize: "0.85rem" }}>
											{bundleLabel(b)}
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ display: "block" }}
										>
											{bundleDetail(b)}
										</Typography>
									</Box>
									{bundleChips(b)}
								</Box>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Bundle"
									placeholder="Select the bundle to reclassify"
								/>
							)}
							sx={{ mb: 1 }}
						/>
					)}

					{touch ? (
						<TabletBundleSummary bundle={selectedBundle} />
					) : (
						<Box sx={{ mb: 1.5 }}>
							<InfoGrid items={bundleInfo} />
						</Box>
					)}

					<TextField
						fullWidth
						size={touch ? "medium" : "small"}
						autoFocus={!touch}
						placeholder="Search part number…"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon
										fontSize="small"
										sx={{ color: "text.disabled" }}
									/>
								</InputAdornment>
							),
						}}
						sx={{ mb: 1.5 }}
					/>

					<Box
						sx={{
							...(touch ? { flex: 1, minHeight: 0 } : { height: 340 }),
							overflow: "auto",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
						}}
					>
						{loading ? (
							<Box
								sx={{
									py: 5,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 1.5,
								}}
							>
								<CircularProgress
									size={24}
									aria-label="Loading reclassification"
								/>
								<Typography color="text.secondary">
									Loading bundle details…
								</Typography>
							</Box>
						) : emptyText ? (
							<Typography
								color="text.secondary"
								sx={{ py: 5, textAlign: "center" }}
							>
								{emptyText}
							</Typography>
						) : (
							<>
								<HeaderRow touch={touch} />
								{filtered.map((p) => (
									<ProductRow
										key={p.ProductId}
										product={p}
										selected={selected?.ProductId === p.ProductId}
										onSelect={toggleSelected}
										touch={touch}
									/>
								))}
							</>
						)}
					</Box>
				</MesfModal.Content>
				<MesfModal.Actions
					style={{ padding: "16px 24px" }}
					sx={touch ? { "& button": { minHeight: 56, px: 2.5 } } : undefined}
				>
					{/* Normal → cutback only; reclassified → both; cutback → restore only */}
					{!reclassification?.IsCutback && (
						<Button
							variant="outlined"
							color="warning"
							disabled={actionsDisabled}
							onClick={() => setConfirmCutback(true)}
						>
							Send to Cutback
						</Button>
					)}
					{reclassification != null && (
						<Button
							variant="outlined"
							color="success"
							disabled={actionsDisabled}
							onClick={() => setConfirmRestore(true)}
						>
							Restore Original Part
						</Button>
					)}
					<Box sx={{ flexGrow: 1 }} />
					<Button variant="contained" color="inherit" onClick={onClose}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="success"
						disabled={selected == null || actionsDisabled}
						onClick={() => void save(false)}
					>
						{saving ? "Saving…" : "Save"}
					</Button>
				</MesfModal.Actions>
			</MesfModal>

			<ConfirmationDialogRaw
				open={open && confirmCutback}
				title="Send to Cutback?"
				message="The bundle will be unlinked from its work order and set aside as material to re-cut. Continue?"
				onClose={(value) => {
					setConfirmCutback(false);
					if (value === "OK") void save(true);
				}}
			/>

			<ConfirmationDialogRaw
				open={open && confirmRestore}
				title="Restore original part?"
				message={
					reclassification?.ConsumedByWorkOrderId != null
						? `This bundle was used by work order ${
								reclassification.ConsumedByWorkOrderNumber ?? "—"
							}${reclassification.ConsumedPartNumber ? ` (${reclassification.ConsumedPartNumber})` : ""}. Restoring removes it from that order and links it back to its original part${
								reclassification.OriginalPartNumber
									? ` (${reclassification.OriginalPartNumber})`
									: ""
							}. Continue?`
						: "This will undo the reclassification and link the bundle back to its original work order. Continue?"
				}
				onClose={(value) => {
					setConfirmRestore(false);
					if (value === "OK") void restore();
				}}
			/>
		</>
	);
};
